import { useMemo, useState } from "react";
import clsx from "clsx";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { PlotCard } from "../PlotCard";
import { useTimezone, utcToLocalNaive } from "../../lib/datetime";

type XAxis = "chainage" | "time";

interface Props {
  fromIso: string;
  toIso: string;
}

/**
 * Cumulative bentonite delivery (m³) and rolling-per-pipe delivery (L)
 * vs chainage or time. Mirrors the example PNG `Volume_by_Chainage.png`.
 */
export function VolumeChart({ fromIso, toIso }: Props) {
  const [xAxis, setXAxis] = useState<XAxis>("chainage");
  const tz = useTimezone();
  const drive = useQuery({ queryKey: ["drive"], queryFn: api.drive });
  const series = useQuery({
    queryKey: ["lubrication", "series", fromIso, toIso],
    queryFn: () => api.lubrication.series({ from: fromIso, to: toIso }),
    refetchInterval: 60_000,
  });

  // Pipe length sets the rolling window for "volume per pipe". Default
  // 4m matching the user's drive geometry.
  const pipeLengthM = drive.data?.pipeSchedule?.pipeLengthM ?? 4;

  const data = useMemo(() => {
    const records = (series.data?.records ?? [])
      .filter((r) => (xAxis === "chainage" ? r.chainageM != null : true))
      .filter((r) => r.actVolM3 != null);

    // Convert m³ to litres for the rolling-per-pipe trace (so it reads
    // in the same units as the example plot's "volume per pipe").
    const cumL = records.map((r) => (r.actVolM3 ?? 0) * 1000);

    // Rolling delta over the last `pipeLengthM` of chainage (or last
    // pipeLengthM minutes if x=time isn't useful here, so we just lag
    // by chainage in either case).
    const xs: (number | string | null)[] = records.map((r) =>
      xAxis === "chainage" ? (r.chainageM ?? null) : (utcToLocalNaive(r.ts, tz) ?? null),
    );
    const rolling: (number | null)[] = records.map((_, i) => {
      const here = records[i]!;
      const hereCh = here.chainageM;
      if (hereCh == null) return null;
      // Walk back to the first sample whose chainage is at least
      // pipeLengthM behind the current one.
      for (let j = i; j >= 0; j--) {
        const prev = records[j]!;
        if (prev.chainageM == null) continue;
        if (hereCh - prev.chainageM >= pipeLengthM) {
          const prevL = (prev.actVolM3 ?? 0) * 1000;
          return cumL[i]! - prevL;
        }
      }
      return null;
    });

    return [
      {
        x: xs,
        y: cumL,
        type: "scatter",
        mode: "lines",
        name: "Total volume (L)",
        line: { color: "#000", width: 1.4 },
        yaxis: "y",
      },
      {
        x: xs,
        y: rolling,
        type: "scatter",
        mode: "lines",
        name: `Volume per ${pipeLengthM} m (L)`,
        line: { color: "#c8102e", width: 1.2 },
        yaxis: "y2",
      },
    ] as Record<string, unknown>[];
  }, [series.data, xAxis, pipeLengthM, tz]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[10.5px] tracking-wider uppercase text-dim">X axis</span>
        <Segmented
          value={xAxis}
          onChange={setXAxis}
          options={[
            { id: "chainage", label: "Chainage" },
            { id: "time", label: "Time" },
          ]}
        />
      </div>
      <PlotCard
        title="Volume delivered"
        subtitle={`Cumulative + rolling per ${pipeLengthM} m`}
        data={data as never}
        height={420}
        // fromIso changes whenever the user changes range, so embedding it
        // here forces Plotly to drop the previous zoom box and autorange.
        uirevision={`vol-${xAxis}-${fromIso}`}
        layout={{
          margin: { l: 56, r: 64, t: 16, b: 36 },
          xaxis:
            xAxis === "time"
              ? { tickformat: "%-d %b", hoverformat: "%-d %b %Y · %H:%M" }
              : { title: { text: "Chainage (m)" } },
          yaxis: { title: { text: "Total volume (L)" }, rangemode: "tozero" },
          yaxis2: {
            title: { text: "Per pipe (L)" },
            overlaying: "y",
            side: "right",
            showgrid: false,
            rangemode: "tozero",
          },
          legend: {
            x: 0.02, y: 0.98, xanchor: "left", yanchor: "top",
            bgcolor: "rgba(255,255,255,0.85)",
            bordercolor: "rgba(0,0,0,0.08)", borderwidth: 1,
            font: { size: 10, color: "#374151" },
          },
          showlegend: true,
        }}
      />
    </div>
  );
}

function Segmented<T extends string>({
  value, onChange, options,
}: { value: T; onChange: (v: T) => void; options: { id: T; label: string }[] }) {
  return (
    <div className="inline-flex bg-surface2 border border-border1 rounded overflow-hidden">
      {options.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          className={clsx(
            "px-3 py-1.5 text-[11px] tracking-wider uppercase transition-colors",
            value === o.id ? "bg-wb text-white" : "text-muted hover:bg-surface3 hover:text-text",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
